import { Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import { HttpHeaders } from '@angular/common/http';
import { ShowIfHttpError } from '@certemy/common';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { VouchersListQueryParam, VouchersListTableData } from './vouchers-list/vouchers-list.interface';
import { Version } from './vouchers-versions/vouchers-versions.interface';
import { createQueryParam, buildQueryParams } from 'utils/helpers';
import { VouchersListFilters } from './vouchers-list/vouchers-list.constants';
import { VouchersListRow } from './vouchers-list/vouchers-list.interface';
import { VouchersData, VoucherCertification } from './vouchers-edit/vouchers-edit.interface';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class VouchersService {
  constructor(private http: HttpService) {}
  voucherToShare!: VouchersListRow | VouchersData;

  currentVoucherSubject = new BehaviorSubject(this.voucherToShare);

  @ShowIfHttpError('Cannot receive vouchers')
  getVouchersList(params: VouchersListQueryParam = new VouchersListFilters()): Observable<VouchersListTableData> {
    const queryParams = this.getQueryParams(params);

    return this.http.get(`/voucher-group${queryParams}`);
  }

  private getQueryParams({ limit, offset, orderId, orderType, search, filters }: VouchersListQueryParam): string {
    return buildQueryParams(
      [
        limit && createQueryParam('limit', limit),
        offset && createQueryParam('offset', offset),
        orderId && createQueryParam('orderId', orderId),
        orderType && createQueryParam('orderType', orderType),
        search && createQueryParam('search', search),
        createQueryParam('filters', filters),
      ].filter(Boolean),
    );
  }

  @ShowIfHttpError('Cannot receive voucher group')
  getVouchers(id: number): Observable<VouchersData> {
    return this.http.get(`/voucher-group/${id}`);
  }

  @ShowIfHttpError('Cannot create voucher group')
  createVouchers(data: VouchersData): Observable<VouchersData> {
    return this.http.post('/voucher-group', data);
  }

  @ShowIfHttpError('Cannot update voucher group')
  updateVouchers(id: string, data: VouchersData): Observable<VouchersData> {
    return this.http.put(`/voucher-group/${id}`, data);
  }

  @ShowIfHttpError('Could not delete voucher group')
  deleteVouchers(vouchersIds: number[]) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: { voucherGroupIds: vouchersIds },
    };
    return this.http.delete(`/voucher-group`, options);
  }

  @ShowIfHttpError('Could not copy voucher group')
  copyVouchers(vouchersIds: number[]) {
    return this.http.post(`/voucher-group/copy`, { voucherGroupIds: vouchersIds });
  }

  @ShowIfHttpError('Could not publish voucher group')
  publishVouchers(vouchersId: number, data: VouchersData): Observable<VouchersData> {
    return this.http.post(`/voucher-group/${vouchersId}/publish`, data);
  }

  @ShowIfHttpError('Could not load voucher versions')
  getVoucherVersions(id: number): Observable<Version[]> {
    return this.http.get(`/voucher-group/${id}/version`);
  }

  @ShowIfHttpError('Cannot receive certifications')
  getVouchersCertificates(query: VouchersListQueryParam): Observable<VoucherCertification[]> {
    const queryParams = this.getQueryParams(query as any);

    return this.http.get(`/voucher-group/certification${queryParams}`).pipe(map((data) => data.rows));
  }

  setCurrentVoucher(voucher: VouchersListRow | VouchersData) {
    this.voucherToShare = voucher;
  }

  getCurrentVoucher(): VouchersListRow | VouchersData {
    return this.voucherToShare;
  }
}
